import { useInjection } from 'inversify-react';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { IOverlayLoaderStore } from '../../../../../../modules/common/OverlayLoader.store';
import { Settings } from '../../../../../../modules/settings/SettingsContext';
import { ExportDataFilter } from '../../../../projects/projects.model';
import { ActivityLog } from '../components/ActivityLog';
import { IProjectDetailsActivitiesBl } from '../projectDetailsActivities.bl';
import { ProjectActivity } from '../projectDetailsActivities.models';

export const ActivityLogContainer = observer((): ReactElement => {
  const projectDetailsActivitiesBl = useInjection(IProjectDetailsActivitiesBl.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);

  const context = useContext(Settings);

  const [actor, setActor] = useState<string>('');

  const params = useParams<{ projectId: string }>();

  const mapProjectActivitiesToStrings = (activitiesTypes: ProjectActivity[]): string[] => {
    return activitiesTypes;
  };

  useEffect(() => {
    void projectDetailsActivitiesBl.getProjectActivitiesAsync(
      params.projectId,
      mapProjectActivitiesToStrings(projectDetailsActivitiesBl.store.selectedProjectActivities),
      actor,
      projectDetailsActivitiesBl.store.projectActivitiesPaging
    );
  }, [params]);

  const handleActorChange = async (actor: string): Promise<void> => {
    setActor(actor);
    await handleSearchChangeDebounced();
  };

  const handleSearchChangeDebounced = useDebouncedCallback(async () => {
    await handleSearchChange();
  }, 1000);

  const handleSearchChange = async (): Promise<void> => {
    await projectDetailsActivitiesBl.getProjectActivitiesAsync(
      params.projectId,
      mapProjectActivitiesToStrings(projectDetailsActivitiesBl.store.selectedProjectActivities),
      actor,
      { ...projectDetailsActivitiesBl.store.projectActivitiesPaging, pageNumber: 1 }
    );
  };

  const handleActivitiesChange = action(async (selectedActivities: ProjectActivity[]): Promise<void> => {
    projectDetailsActivitiesBl.store.setSelectedActivities(selectedActivities);
    await projectDetailsActivitiesBl.getProjectActivitiesAsync(params.projectId, selectedActivities, actor, {
      ...projectDetailsActivitiesBl.store.projectActivitiesPaging,
      pageNumber: 1,
    });
  });

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    projectDetailsActivitiesBl.store.projectActivitiesPaging.pageNumber = pageNumber;
    projectDetailsActivitiesBl.store.projectActivitiesPaging.pageSize = pageSize;
    context.setActivityLogPageSize(pageSize);

    await projectDetailsActivitiesBl.getProjectActivitiesAsync(
      params.projectId,
      mapProjectActivitiesToStrings(projectDetailsActivitiesBl.store.selectedProjectActivities),
      actor,
      projectDetailsActivitiesBl.store.projectActivitiesPaging
    );
  };

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    projectDetailsActivitiesBl.store.projectActivitiesPaging.orderBy = orderBy;
    projectDetailsActivitiesBl.store.projectActivitiesPaging.orderType = orderType || 'asc';
    await projectDetailsActivitiesBl.getProjectActivitiesAsync(
      params.projectId,
      mapProjectActivitiesToStrings(projectDetailsActivitiesBl.store.selectedProjectActivities),
      actor,
      projectDetailsActivitiesBl.store.projectActivitiesPaging
    );
  };

  const handleExportDownload = async (backupId: string, schemaVersion: string, dataFilter?: ExportDataFilter): Promise<void> => {
    overlayLoader.enableLoader('project-export-button');
    await projectDetailsActivitiesBl.downloadExportAsync(params.projectId, backupId, dataFilter);
    overlayLoader.disableLoader('project-export-button');
  };

  return (
    <ActivityLog
      activities={projectDetailsActivitiesBl.store.projectActivities}
      isLoading={projectDetailsActivitiesBl.store.showLoader}
      searchValue={actor}
      selectedActivities={projectDetailsActivitiesBl.store.selectedProjectActivities}
      paging={{ ...projectDetailsActivitiesBl.store.projectActivitiesPaging, pageSize: context.store.activityLogPageSize }}
      onActorChange={handleActorChange}
      onActivitiesChange={handleActivitiesChange}
      onPaginationChange={handlePaginationChange}
      onOrderingChange={handleOrderingChange}
      onExportDownload={handleExportDownload}
    />
  );
});
